import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatcher } from 'src/redux/store';
import { updateIssuedChequeStatus } from 'src/redux/slices/issued-cheque';

import Label from 'src/components/label';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import {
  IIssuedChequeItem,
  IssuedChequeStatus,
  ISSUED_CHEQUE_NEXT_STATUSES,
  getIssuedChequeStatusColor,
} from 'src/types/issued-cheque';

const HINTS: Record<string, string> = {
  [IssuedChequeStatus.REALIZED]: 'The payee banked the cheque and the money left our account.',
  [IssuedChequeStatus.RETURNED]:
    'The bank returned the cheque. You can then pay the payee instead.',
  [IssuedChequeStatus.HOLDING]:
    'The cheque will not be banked (e.g. the payee lost it). You can then pay the payee instead.',
};

const schema = Yup.object().shape({
  status: Yup.mixed<IssuedChequeStatus>()
    .oneOf(ISSUED_CHEQUE_NEXT_STATUSES)
    .required('Status is required'),
  date: Yup.date().required('Date is required'),
  note: Yup.string().max(500, 'Note must be at most 500 characters'),
});

type Props = {
  cheque: IIssuedChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

export default function IssuedChequeStatusDialog({ cheque, isOpen, onClose, onSuccess }: Props) {
  const dispatch = useAppDispatcher();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { status: ISSUED_CHEQUE_NEXT_STATUSES[0], date: new Date(), note: '' },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isSubmitting },
  } = methods;

  const status = watch('status');

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        updateIssuedChequeStatus({
          id: cheque._id,
          status: data.status,
          date: new Date(data.date),
          note: data.note || undefined,
        })
      ).unwrap();

      enqueueSnackbar(`Cheque marked ${data.status}`);
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to change the status', {
        variant: 'error',
      });
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Change Status</DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            What happened to cheque <strong>{cheque.chequeNumber}</strong>?
          </Typography>

          <Stack spacing={2}>
            <RHFSelect fullWidth name="status" label="Status" required>
              {ISSUED_CHEQUE_NEXT_STATUSES.map((option) => (
                <MenuItem key={option} value={option}>
                  <Label variant="soft" color={getIssuedChequeStatusColor(option)}>
                    {option}
                  </Label>
                </MenuItem>
              ))}
            </RHFSelect>

            <Typography variant="caption" color="text.secondary">
              {HINTS[status]}
            </Typography>

            <Controller
              name="date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, error: !!error, helperText: error?.message },
                  }}
                />
              )}
            />

            <RHFTextField name="note" label="Note (optional)" multiline rows={2} />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
